import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mightycleaningservices.co.uk";

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${siteUrl}/about#webpage`,
  "url": `${siteUrl}/about`,
  "name": "About Mighty Cleaning Services Ltd | Window Cleaners Northfleet & Kent",
  "description":
    "Learn about Mighty Cleaning Services Ltd — over 10 years delivering professional window cleaning, gutter clearance and exterior cleaning in Northfleet, Gravesend and Kent. Fully insured, DBS-checked team.",
  "inLanguage": "en-GB",
  "isPartOf": {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "name": "Mighty Cleaning Services Ltd",
    "url": siteUrl,
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About Us",
        "item": `${siteUrl}/about`,
      },
    ],
  },
  "about": {
    "@type": "LocalBusiness",
    "name": "Mighty Cleaning Services Ltd",
    "foundingDate": "2014",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": 10,
    },
    "areaServed": [
      "Northfleet",
      "Gravesend",
      "Dartford",
      "Swanscombe",
      "Ebbsfleet",
      "Greenhithe",
      "Longfield",
      "Kent",
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5",
    },
  },
};

// ... existing code (values array) ...

const values = [
  {
    icon: "ri-shield-check-fill",
    title: "Fully Insured",
    desc: "We carry comprehensive public liability insurance on every job, so you're fully protected.",
  },
  {
    icon: "ri-group-fill",
    title: "Experienced Team",
    desc: "Our cleaners are trained professionals with over 10 years of experience in window and glass cleaning.",
  },
  {
    icon: "ri-leaf-fill",
    title: "Eco-Friendly",
    desc: "We use purified water systems and biodegradable cleaning solutions — safe for your family and the environment.",
  },
  {
    icon: "ri-customer-service-fill",
    title: "Excellent Customer Care",
    desc: "Our friendly team is always on hand to answer questions, adjust schedules, and ensure you're fully satisfied.",
  },
];

export default function AboutPage() {
  return (
    <div className="font-body">
      <SEOHead
        title="About Mighty Cleaning Services Ltd | Window Cleaners Northfleet & Kent"
        description="Over 10 years delivering professional window cleaning, gutter clearance and exterior cleaning across Northfleet, Gravesend and Kent. Fully insured, DBS-checked, 4.9★ on Google."
        keywords="about Mighty Cleaning Services, window cleaners Northfleet, professional window cleaning Kent, gutter clearance Gravesend, local cleaning company Kent"
        canonical={`${siteUrl}/about`}
      />
      <JsonLd id="about-webpage" data={aboutJsonLd} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_569d4350cbfe316d514ff41030f45e35_5b5bbb68.jpg"
              alt="Mighty Cleaning Services team Northfleet"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2040]/90 via-[#0F2040]/75 to-[#0F2040]/50"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
            <div className="max-w-2xl">
              <span className="text-[#60AEFF] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
                About Us
              </span>
              <h1 className="font-heading font-800 text-white leading-tight mb-5" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                Trusted Window Cleaning Experts
              </h1>
              <p className="text-blue-100 font-body text-lg leading-relaxed mb-8">
                Mighty Cleaning Services Ltd has been delivering professional window cleaning across Northfleet, Gravesend, Kent and surrounding areas for over a decade. We&apos;re proud to be the local team you can count on.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/#contact-us"
                  className="flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get a Free Quote
                </a>
                <a
                  href="tel:02084322244"
                  className="flex items-center gap-2 bg-white/15 border border-white/30 text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap hover:bg-white/25"
                >
                  <i className="ri-phone-fill text-[#60AEFF]"></i>
                  0208 43 222 44
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/8f336d42efbdad3c2be40e7684404e66.jpg"
                  alt="Mighty Cleaning Services professional window cleaning Northfleet"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
                  Our Story
                </span>
                <h2
                  className="font-heading font-800 text-[#0F2040] leading-tight mb-6"
                  style={{ fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 800 }}
                >
                  A Decade of Professional Window Cleaning in Northfleet
                </h2>
                <div className="space-y-4 text-gray-600 font-body text-base leading-relaxed">
                  <p>
                    Mighty Cleaning Services Ltd was founded right here in Northfleet with a simple mission: to provide the most reliable, thorough, and professional <strong className="text-[#0F2040]">window cleaning Northfleet</strong> residents and businesses could depend on.
                  </p>
                  <p>
                    Over the years, we&apos;ve grown into one of the most trusted names for <strong className="text-[#0F2040]">window cleaners Gravesend</strong> and the wider area — serving hundreds of homes, offices, retail units, and commercial properties with consistent, high-quality results.
                  </p>
                  <p>
                    Our team is fully trained, DBS-checked, and equipped with the latest water-fed pole technology and purified water systems to deliver a streak-free, spotless finish every single time.
                  </p>
                </div>
                <div className="mt-8 grid grid-cols-3 gap-4">
                  {[
                    { value: "10+", label: "Years Trading" },
                    { value: "500+", label: "Customers Served" },
                    { value: "4.9★", label: "Google Rating" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#EBF4FF] rounded-xl p-4 text-center">
                      <div className="font-heading font-800 text-[#1A3A6B] text-2xl mb-1" style={{ fontWeight: 800 }}>{stat.value}</div>
                      <div className="text-gray-500 font-body text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-[#F8FAFE]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
                Our Values
              </span>
              <h2
                className="font-heading font-800 text-[#0F2040] leading-tight"
                style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", fontWeight: 800 }}
              >
                What Sets Us Apart
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-7 border border-gray-100 flex items-start gap-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#EBF4FF] rounded-xl flex-shrink-0">
                    <i className={`${v.icon} text-[#1E88E5] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-700 text-[#0F2040] text-lg mb-2" style={{ fontWeight: 700 }}>{v.title}</h3>
                    <p className="text-gray-500 font-body text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage Area */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
                  Local Coverage
                </span>
                <h2
                  className="font-heading font-800 text-[#0F2040] leading-tight mb-6"
                  style={{ fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 800 }}
                >
                  Serving Northfleet, Gravesend &amp; Beyond
                </h2>
                <p className="text-gray-600 font-body text-base leading-relaxed mb-8">
                  We cover a wide area across Kent, including Gravesend, Dartford, Swanscombe, Bluewater, Ebbsfleet, and surrounding towns and villages.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {["Northfleet","Gravesend","Dartford","Swanscombe","Bluewater","Ebbsfleet","Greenhithe","Longfield"].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-gray-700">
                      <i className="ri-map-pin-2-fill text-[#1E88E5] text-sm"></i>
                      <span className="font-body text-sm">{area}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 font-body text-sm mt-5 italic">
                  Not sure if we cover your area? Give us a call — we&apos;re always happy to help.
                </p>
              </div>
              <div className="w-full h-80 rounded-2xl overflow-hidden border border-gray-100">
                <iframe
                  title="Service area map for window cleaning Northfleet Gravesend"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24843!2d0.3398!3d51.4457!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8b4b9aef5d10d%3A0x0!2sNorthfleet%2C+Gravesend!5e0!3m2!1sen!2suk!4v1700000000001!5m2!1sen!2suk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  aria-label="Map of Northfleet and Gravesend service area"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-16 bg-[#EBF4FF]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-heading font-700 text-[#0F2040] text-xl mb-1" style={{ fontWeight: 700 }}>
                Ready to work with Northfleet&apos;s best window cleaners?
              </h3>
              <p className="text-gray-500 font-body text-sm">Call or request a free quote — no obligation.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="/#contact-us"
                className="flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Get a Free Quote
              </a>
              <a
                href="tel:02084322244"
                className="flex items-center gap-2 bg-white border border-gray-200 text-[#1A3A6B] font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap hover:bg-[#EBF4FF]"
              >
                <i className="ri-phone-fill text-[#1E88E5]"></i>
                0208 43 222 44
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
